import { ref, computed } from 'vue';
import type { Address } from 'viem';
import { createPublicClient, http, formatUnits } from 'viem';
import { erc20Abi } from '../lib/abi';
import { useWalletStore } from '../stores/wallet';
import { useChainStore } from '../stores/chain';

type Token = {
  chainId: number;
  address: Address;
  name: string;
  symbol: string;
  decimals: number;
  logoURI?: string;
};

const tokens = ref<Token[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const selectedFrom = ref<Token | null>(null);
const selectedTo = ref<Token | null>(null);
const balances = ref<Record<string, string>>({}); // address -> formatted balance
const serverMeta = ref<{ cached?: boolean; updatedAt?: string | null; nextRefreshAt?: string | null; cooldownSecondsRemaining?: number } | null>(null);

function getClient() {
  const chain = useChainStore();
  const cfg = chain.current;
  if (!cfg.rpcUrl) throw new Error('RPC URL not configured for selected chain');
  return createPublicClient({ chain: cfg.chain, transport: http(cfg.rpcUrl) });
}

export function useTokens() {
  const wallet = useWalletStore();

  async function load(forceRefresh = false) {
    // Reload tokens whenever chain changes or wallet connects
    const chain = useChainStore();
    tokens.value = [];
    balances.value = {};
    loading.value = true;
    try {
      // If wallet connected, try server-side discovery first
      if (wallet.isConnected && wallet.address) {
        const url = `/api/balances?address=${wallet.address}&chainId=${chain.current.id}${forceRefresh ? '&refresh=1' : ''}`;
        const res = await fetch(url);
        if (res.ok) {
          const json = await res.json();
          serverMeta.value = json.meta || null;
          const list = Array.isArray(json.tokens) ? json.tokens : [];
          if (list.length > 0) {
            tokens.value = list;
            // Fill balances from server response (raw), format with decimals
            const raw = (json.balances || {}) as Record<string, string>;
            for (const t of tokens.value) {
              const k = t.address as string;
              const v = raw[k];
              if (v) {
                try { balances.value[k] = formatUnits(BigInt(v), t.decimals); } catch {}
              }
            }
          }
        }
      }
      // Fallback to static tokenlist if no wallet tokens found
      if (tokens.value.length === 0) {
        const res = await fetch('/tokenlist.json');
        const json = await res.json();
        tokens.value = (json.tokens || []).filter((t: any) => t.chainId === chain.current.id);
      }
      if (tokens.value.length >= 1) selectedFrom.value = tokens.value[0];
      if (tokens.value.length >= 2) selectedTo.value = tokens.value[1];
    } catch (e: any) {
      error.value = e?.message || 'Failed to load tokens';
    } finally {
      loading.value = false;
    }
  }

  async function refreshBalances() {
    balances.value = {};
    if (!wallet.isConnected || !wallet.address) return;
    const pc = getClient();
    for (const t of tokens.value) {
      try {
        const bal = await pc.readContract({
          address: t.address,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [wallet.address]
        });
        balances.value[t.address] = formatUnits(bal as bigint, t.decimals);
      } catch (e) {
        // ignore token balance failures
      }
    }
  }

  const fromBalance = computed(() => (selectedFrom.value ? balances.value[selectedFrom.value.address] || '0' : '0'));
  const toBalance = computed(() => (selectedTo.value ? balances.value[selectedTo.value.address] || '0' : '0'));
  
  async function refreshServerTokens() {
    await load(true);
  }

  return { tokens, loading, error, selectedFrom, selectedTo, balances, fromBalance, toBalance, load, refreshBalances, serverMeta, refreshServerTokens };
}
